import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Icon } from '@/components/Icon';
import * as utils from '@/utils';
import * as context from './context';
/** Composable TabGroup components that provides the children with the tab context. */
export const TabGroup = tsx.componentFactoryOf().create({
    name: 'tab-group',
    props: {
        tag: p(String).default('div'),
        /**
         * Control the initial tab to be shown.
         * Useful when the component is not used in a controlled manner.
         */
        defaultTab: p.ofType().required,
        /**
         * Pass selectedTab to use component as a controlled component;
         * that is, the parent component will manage the TabGrou's state.
         */
        selectedTab: p.ofType().optional,
    },
    /** @returns The provided data. */
    provide() {
        return {
            [context.TabContextKey]: () => ({
                id: this.id,
                selectedTab: this.realSelectedTab,
                setSelectedTab: this.setSelectedTab,
                getTabButtonId: idx => `${this.id}-btn-${String(idx)}`,
                getTabPanelId: idx => `${this.id}-panel-${String(idx)}`,
                isSelected: idx => this.realSelectedTab === idx,
            }),
        };
    },
    /** @returns Internal data of the component. */
    data() {
        return {
            id: `cg-tabs-${utils.getUniqueId()}`,
            internalSelectedTab: this.defaultTab,
        };
    },
    computed: {
        /** @returns The current selected tab, considering both internal and external state. */
        realSelectedTab() {
            return this.selectedTab != null ? this.selectedTab : this.internalSelectedTab;
        },
    },
    methods: {
        /**
         * Sets the selected tab and emits the onChange event.
         *
         * @param tab The new tab to select.
         */
        setSelectedTab(tab) {
            this.internalSelectedTab = tab;
            tsx.emitOn(this, 'onChange', tab);
        },
    },
    /** @returns Body of the component. */
    render() {
        return this.$createElement(this.tag, {
            attrs: { id: this.id },
        }, this.$slots.default);
    },
});
/** Composable TabList components. */
export const TabList = tsx.component({
    name: 'tab-list',
    functional: true,
    props: {
        tag: p(String).default('nav'),
        variant: p.ofStringLiterals('button-group', 'unstyled').default('unstyled'),
    },
    /** @returns Body of the component. */
    render(h, { props: { tag, variant }, slots, data: { attrs }, data }) {
        return h(tag, {
            attrs: {
                role: 'tablist',
                'data-variant': variant,
                ...attrs,
            },
            class: [
                'group/tab-list',
                {
                    unstyled: '',
                    'button-group': [
                        'grid grid-flow-col auto-cols-fr gap-1 p-1 rounded-md bg-neutral-10 dark:bg-neutral-65',
                    ],
                }[variant],
                data.class,
            ],
            staticClass: data.staticClass,
        }, slots().default);
    },
});
/** Composable Tab Button component. */
export const TabButton = tsx.withHtmlAttrs(tsx.component({
    name: 'tab',
    inject: context.injectTabContext(),
    props: {
        tabName: p.ofType().required,
        icon: p.ofType().optional,
        label: p(String).required,
        variant: p.ofStringLiterals('button-group', 'unstyled').default('unstyled'),
    },
    computed: {
        ...context.useTabContextValue(),
        /** @returns True if this is selected. */
        isSelected() {
            return this.tabContext.isSelected(this.tabName);
        },
        /** @returns Classes of the component. */
        classes() {
            return [
                'cg-tab-button',
                {
                    unstyled: '',
                    'button-group': [
                        'cg-btn min-h-8',
                        'border-none',
                        this.isSelected
                            ? ['shadow-sm btn-actionable-4 not-dark:bg-white']
                            : ['btn-neutral-1'],
                    ],
                }[this.variant],
            ];
        },
        /** @returns Classes of the text label. */
        labelClasses() {
            if (this.variant === 'unstyled') {
                return undefined;
            }
            return 'grow';
        },
        /** @returns The icon node. */
        iconNode() {
            if (this.icon == null) {
                return utils.emptyVNode();
            }
            return <Icon icon={this.icon}/>;
        },
    },
    /** @returns Body of the component. */
    render() {
        const { tabContext: ctx, tabName, isSelected } = this;
        return (<button role="tab" class={this.classes} type="button" id={ctx.getTabButtonId(tabName)} aria-selected={isSelected} aria-controls={ctx.getTabPanelId(tabName)} onClick={() => {
                ctx.setSelectedTab(tabName);
            }}>
                    {this.iconNode}
                    <span class={this.labelClasses}>{this.label}</span>
                </button>);
    },
}));
/** Composable TabPanel components. */
export const TabPanel = tsx.withHtmlAttrs(tsx.component({
    name: 'tab-panel',
    inject: context.injectTabContext(),
    props: {
        tabName: p.ofType().required,
        tag: p(String).default('div'),
    },
    computed: {
        ...context.useTabContextValue(),
    },
    /** @returns Body of the component. */
    render() {
        const { tabContext: ctx, tabName } = this;
        const selected = ctx.isSelected(tabName);
        return this.$createElement(this.tag, {
            attrs: {
                role: 'tabpanel',
                id: ctx.getTabPanelId(tabName),
                'aria-labelledby': ctx.getTabButtonId(tabName),
                inert: !selected,
            },
            class: [{ hidden: !selected }],
        }, this.$slots.default);
    },
}));
